'use client';
import { loadFont } from '@remotion/fonts';
import { staticFile } from 'remotion';

import {
  FONTS_TO_LOAD,
  FONT_DISPLAY_NAMES,
  FONT_FAMILY_CSS_VARIABLES,
  FONT_FAMILY_LIST,
  FONT_WEIGHTS_BY_FAMILY,
  getFontDefaultWeight,
  getFontLoadEntries,
  type FontFamilyValue,
  type FontWeight,
} from '../../scripts/local-font-data';

// local-font-data에서 re-export
export {
  FONT_DISPLAY_NAMES,
  FONT_FAMILY_CSS_VARIABLES,
  FONT_FAMILY_LIST,
  FONT_WEIGHTS_BY_FAMILY as FONT_WEIGHTS_BY_FAMILY_FALLBACK,
  getFontDefaultWeight,
  type FontFamilyValue,
};

// 로컬 폰트 로드 - 브라우저 환경에서만 실행
if (typeof window !== 'undefined') {
  const fontEntries = getFontLoadEntries();

  Promise.all(
    fontEntries.map((entry) =>
      loadFont({
        family: entry.family,
        url: staticFile(`/${entry.path}`),
        weight: entry.weight,
      })
        .then(() => {
          // console.log(`Font ${entry.family} (${entry.weight}) loaded!`);
        })
        .catch((error) => {
          console.error(
            `Font ${entry.family} (${entry.weight}) failed to load:`,
            error,
          );
        }),
    ),
  )
    .then(() => {
      console.log('All fonts loading attempted');
    })
    .catch((error) => {
      console.error('Error during font loading:', error);
    });
}

// 폰트 패밀리 정의 상수들 (기존 코드 호환성)
const notoSansKrFontFamily = 'Noto Sans KR';

// FontFamilyValue로 css font-family 값 가져오기 함수
export function fontFamilyToCssVariable(name: FontFamilyValue): string {
  return FONT_FAMILY_CSS_VARIABLES[name] || notoSansKrFontFamily;
}

// 메모리 캐시
const fontCache = new Map<string, FontWeight[]>();

// 특정 폰트에서 지원하는 weight 목록을 가져오는 함수
export async function getSupportedWeights(
  fontFamily: FontFamilyValue,
): Promise<FontWeight[]> {
  console.log('🔍 getSupportedWeights called for:', fontFamily);

  // 캐시 확인
  if (fontCache.has(fontFamily)) {
    const cached = fontCache.get(fontFamily)!;
    console.log('📦 Found in cache:', cached);
    return cached;
  }

  // 로컬 폰트의 weight 정보 반환
  const weights = FONT_WEIGHTS_BY_FAMILY[fontFamily] || [400];

  // 캐시에 저장
  fontCache.set(fontFamily, weights as FontWeight[]);

  return weights as FontWeight[];
}

// 동기 버전 (캐시에 있는 경우만 반환, 없으면 fallback)
export function getSupportedWeightsSync(
  fontFamily: FontFamilyValue,
): FontWeight[] {
  if (fontCache.has(fontFamily)) {
    const cached = fontCache.get(fontFamily)!;
    console.log('📦 Found in cache:', cached);
    return cached;
  }

  const weights = FONT_WEIGHTS_BY_FAMILY[fontFamily];

  if (!weights) {
    console.warn('⚠️ No weight data found for font:', fontFamily);
    console.log('🔄 Using default [400]');
    fontCache.set(fontFamily, [400]);
    return [400];
  }

  console.log('✅ Using weight data:', weights);

  // 캐시에 저장
  fontCache.set(fontFamily, weights as FontWeight[]);

  return weights as FontWeight[];
}

// 폰트가 특정 weight를 지원하는지 확인하는 함수
export function isFontWeightSupported(
  fontFamily: FontFamilyValue,
  weight: number,
): boolean {
  const supportedWeights = getSupportedWeightsSync(fontFamily);
  return supportedWeights.includes(weight as FontWeight);
}

// 폰트 변경 시 지원하지 않는 weight인 경우 가장 가까운 weight로 변경하는 함수
export function getClosestSupportedWeight(
  fontFamily: FontFamilyValue,
  currentWeight: number,
): number {
  const supportedWeights = getSupportedWeightsSync(fontFamily);

  if (supportedWeights.includes(currentWeight as FontWeight)) {
    return currentWeight;
  }

  // 배열이 비어있으면 기본값 반환
  if (supportedWeights.length === 0) {
    return 400;
  }

  // 가장 가까운 weight 찾기
  let closest = supportedWeights[0]!;
  let minDiff = Math.abs(currentWeight - closest);

  for (const weight of supportedWeights) {
    const diff = Math.abs(currentWeight - weight);
    if (diff < minDiff) {
      minDiff = diff;
      closest = weight;
    }
  }

  return closest;
}

// 모든 폰트의 weight 정보를 미리 로드하는 함수
export async function preloadAllFontWeights(): Promise<void> {
  const fontNames = FONTS_TO_LOAD.map((f) => f.family) as FontFamilyValue[];

  // 모든 폰트에 대해 weight 데이터를 캐시에 저장
  fontNames.forEach((fontName) => {
    if (!fontCache.has(fontName)) {
      const weights = FONT_WEIGHTS_BY_FAMILY[fontName] || [400];
      fontCache.set(fontName, weights as FontWeight[]);
      console.log(`💾 Cached weights for ${fontName}:`, weights);
    }
  });
}
